import { useCallback, useEffect, useState } from "react";
import { sessionService } from "../services/sessionService";
import {
  AthleteProgram,
  programService,
} from "../services/programService";
import { Session } from "../types/post";
import { addDays, getLocalDateString } from "../components/schedule/scheduleDateUtils";

/**
 * Programs cap at 12 weeks, so ±14 weeks around today catches every program
 * whose window overlaps now (Dashboard card) without reading the whole
 * session history.
 */
const AROUND_TODAY_WEEKS = 14;

export type AthleteProgramsScope = "all" | "aroundToday";

interface UseAthleteProgramsResult {
  programs: AthleteProgram[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** Patch a session locally (optimistic reschedule / status change). */
  patchSession: (sessionId: string, updates: Partial<Session>) => void;
  removeSession: (sessionId: string) => void;
}

const recompute = async (
  userId: string,
  sessions: (Session & { id: string })[],
): Promise<AthleteProgram[]> => programService.getAthletePrograms(userId, sessions);

/**
 * Loads the athlete's sessions once (all, or a window around today) and
 * derives the programs they belong to (plan 3g). Coach hydration is done by
 * `sessionService`, so the assigner name comes for free.
 */
export function useAthletePrograms(
  userId: string | undefined,
  scope: AthleteProgramsScope = "all",
): UseAthleteProgramsResult {
  const [sessions, setSessions] = useState<(Session & { id: string })[]>([]);
  const [programs, setPrograms] = useState<AthleteProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      let loaded: (Session & { id: string })[];
      if (scope === "aroundToday") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        loaded = await sessionService.getSessionsForDateRange(
          userId,
          getLocalDateString(addDays(today, -AROUND_TODAY_WEEKS * 7)),
          getLocalDateString(addDays(today, AROUND_TODAY_WEEKS * 7)),
        );
      } else {
        loaded = await sessionService.getAllSessions(userId);
      }
      setSessions(loaded);
      setPrograms(await recompute(userId, loaded));
    } catch (loadError) {
      console.error("Error loading programs:", loadError);
      setError("Failed to load programs. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [userId, scope]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const applyLocal = useCallback(
    (next: (Session & { id: string })[]) => {
      setSessions(next);
      if (!userId) return;
      // Program docs are already cached per id in the previous result, but
      // recomputing through the service keeps one code path; the doc reads
      // are single getDocs and cheap.
      void recompute(userId, next)
        .then(setPrograms)
        .catch((e) => console.error("Error recomputing programs:", e));
    },
    [userId],
  );

  const patchSession = useCallback(
    (sessionId: string, updates: Partial<Session>) => {
      applyLocal(
        sessions.map((s) => (s.id === sessionId ? { ...s, ...updates } : s)),
      );
    },
    [sessions, applyLocal],
  );

  const removeSession = useCallback(
    (sessionId: string) => {
      applyLocal(sessions.filter((s) => s.id !== sessionId));
    },
    [sessions, applyLocal],
  );

  return { programs, loading, error, reload, patchSession, removeSession };
}
